import { useReferenceRecentApprovedInvoiceReports } from '@/context/services/reportsType/invoiceReports/Edit.service';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import type { InlineNotificationProps } from '@moneyforward/ap-frontend-components';
import { DATE_FORMAT } from '@moneyforward/ap-frontend-components';
import type { ColumnType } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useMemo } from 'react';

type InvoiceReportItem = {
  id: string;
  number_without_prefix: string;
  title: string;
  office_member?: { display_name?: string };
  created_at?: string;
  invoice_file?: { id?: string; name?: string; cache_content_url?: string };
};

export interface PaymentRequestRow {
  id: string;
  numberWithoutPrefix: string;
  title: string;
  payee: string;
  createdAt: string;
  invoiceId: string;
  mfFileId: string;
  fileName: string;
  fileUrl: string | undefined;
}

export const formatPaymentRequestRow = (
  item: InvoiceReportItem
): PaymentRequestRow => {
  return {
    id: item.id,
    numberWithoutPrefix: item.number_without_prefix,
    title: item.title,
    payee: item.office_member?.display_name || '',
    createdAt: item.created_at
      ? dayjs(item.created_at).format(DATE_FORMAT)
      : '',
    invoiceId: item.id,
    mfFileId: item.invoice_file?.id || '',
    fileName: item.invoice_file?.name || '',
    fileUrl: item.invoice_file?.cache_content_url || undefined,
  };
};

export const usePaymentRequestTable = (payeeId?: string) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useReferenceRecentApprovedInvoiceReports(
    payeeId || ''
  );

  const tableData: PaymentRequestRow[] = useMemo(
    () => (data?.data?.invoice_reports || []).map(formatPaymentRequestRow),
    [data]
  );

  const columns: ColumnType<PaymentRequestRow>[] = useMemo(
    () => [
      {
        dataIndex: 'numberWithoutPrefix',
        title: t('payment_past_details_number_without_prefix'),
      },
      {
        dataIndex: 'title',
        title: t('payment_past_details_title'),
        width: 280,
      },
      {
        dataIndex: 'payee',
        title: t('payment_past_details_payee'),
        width: 280,
      },
      {
        dataIndex: 'createdAt',
        title: t('payment_past_details_uploaded_at'),
        width: 100,
      },
    ],
    [t]
  );

  const notification: InlineNotificationProps = useMemo(
    () => ({
      visible: isCommonError(error ?? {}),
      type: 'error',
      message:
        isCommonError(error ?? {}) && Array.isArray(error?.messages)
          ? error?.messages.join('')
          : String(error?.messages) ?? t('system_error'),
    }),
    [error, t]
  );

  return {
    columns,
    tableData,
    isLoading,
    notification,
  };
};
